import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../../api/api";
import { toast } from "react-toastify";
import SEOSection from "../../components/SEOSection/SEOSection";

const fieldConfigs = {
  title: {
    type: "title",
    label: "Event Title",
    placeholder: "Enter event title",
    isTextarea: false,
  },
  location: {
    type: "location",
    label: "Event Location",
    placeholder: "Enter event location (e.g., Australia)",
    isTextarea: false,
  },
  date: {
    type: "date",
    label: "Event Date",
    placeholder: "Enter event date (e.g., 20 March 2025)",
    isTextarea: false,
  },
  time: {
    type: "time",
    label: "Event Time",
    placeholder: "Enter event time (e.g., 08am-05pm)",
    isTextarea: false,
  },
  mainImage: {
    type: "mainImage",
    label: "Main Event Image",
    placeholder: "Enter main image URL",
    isTextarea: false,
  },
  subtitle: {
    type: "subtitle",
    label: "Event Subtitle",
    placeholder: "Enter event subtitle",
    isTextarea: false,
  },
  description: {
    type: "description",
    label: "Event Description",
    placeholder: "Enter event description",
    isTextarea: true,
  },
  keyFeatures: {
    type: "keyFeatures",
    label: "Key Feature",
    placeholder: "Enter key feature",
    isTextarea: false,
  },
  keyFeaturesSubtitle: {
    type: "keyFeaturesSubtitle",
    label: "Key Features Subtitle",
    placeholder:
      "Enter key features subtitle (e.g., Exclusive event showcasing...)",
    isTextarea: false,
  },
  additionalImage: {
    type: "additionalImage",
    label: "Additional Image",
    placeholder: "Enter additional image URL",
    isTextarea: false,
  },
  venueLocation: {
    type: "venueLocation",
    label: "Venue Address",
    placeholder: "Enter full venue address",
    isTextarea: false,
  },
  contactPhone: {
    type: "contactPhone",
    label: "Contact Phone",
    placeholder: "Enter contact phone number",
    isTextarea: false,
  },
  contactEmail: {
    type: "contactEmail",
    label: "Contact Email",
    placeholder: "Enter contact email",
    isTextarea: false,
  },
  mapUrl: {
    type: "mapUrl",
    label: "Google Map URL",
    placeholder: "Enter Google Maps embed URL",
    isTextarea: false,
  },
  tag: {
    type: "tag",
    label: "Tag",
    placeholder: "Enter tag",
    isTextarea: false,
  },
};

const FieldInput = ({
  id,
  type,
  label,
  index,
  placeholder,
  isTextarea,
  removeField,
  dragListeners,
  depth,
  data,
}) => {
  const fieldName = ["mainImage", "additionalImage"].includes(type)
    ? `image-${type}-${id}`
    : `${type}-${id}`;

  return (
    <div
      className={`mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm ${
        depth > 0 ? "border-primary-500" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div {...dragListeners} className="cursor-grab mr-2 text-gray-500">
            ≡
          </div>
          <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
            {label} {index !== null ? index + 1 : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={removeField}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
        >
          Remove
        </button>
      </div>
      {["mainImage", "additionalImage"].includes(type) ? (
        <>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            URL (optional)
          </label>
          <input
            type="url"
            id={`url-${type}-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
            placeholder={`${placeholder} (optional)`}
            defaultValue={data && !data.startsWith("/uploads/") ? data : ""}
          />
          <div className="mt-3">
            <label
              htmlFor={fieldName}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Or Upload File (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              id={fieldName}
              name={fieldName}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200"
            />
          </div>
          {data && data.startsWith("/uploads/") && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preview
              </label>
              <img
                src={`${process.env.REACT_APP_BACKEND_API_URL}${data}`}
                alt="Preview"
                style={{ maxWidth: "200px" }}
                onError={(e) => (e.target.src = "/fallback-image.png")}
              />
            </div>
          )}
        </>
      ) : isTextarea ? (
        <textarea
          id={`${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data || ""}
          required
        />
      ) : (
        <input
          type="text"
          id={`${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data || ""}
          required
        />
      )}
    </div>
  );
};

const TreeItem = ({
  item,
  depth,
  removeField,
  allFieldsItems,
  activeId,
  projected,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isMeta = item.type === "metaTitle" || item.type === "metaDescription";
  const sameTypeFields = allFieldsItems.filter((f) => f.type === item.type);
  const index = isMeta
    ? null
    : sameTypeFields.findIndex((f) => f.id === item.id);
  const currentDepth =
    activeId === item.id && projected ? projected.depth : depth;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="mb-2">
      <FieldInput
        id={item.id}
        type={item.type}
        label={item.label}
        index={index}
        placeholder={item.placeholder}
        isTextarea={item.isTextarea}
        removeField={() => removeField(item.id)}
        dragListeners={listeners}
        depth={currentDepth}
        data={item.data}
      />
      {Array.isArray(item.children) && item.children.length > 0 && (
        <div className="ml-6">
          <SortableContext
            id={`sortable-${item.id}`}
            items={item.children.map((child) => child.id)}
            strategy={verticalListSortingStrategy}
          >
            {item.children.map((child) => (
              <TreeItem
                key={child.id}
                item={child}
                depth={depth + 1}
                removeField={removeField}
                allFieldsItems={allFieldsItems}
                activeId={activeId}
                projected={projected}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

function flattenTree(items, parent = null, depth = 0) {
  return items.reduce((acc, item) => {
    const newItem = { ...item, parent, depth };
    acc.push(newItem);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenTree(item.children, item.id, depth + 1));
    }
    return acc;
  }, []);
}

function buildTree(flattenedItems) {
  const idMapping = flattenedItems.reduce((acc, item) => {
    acc[item.id] = {
      id: item.id,
      type: item.type,
      label: item.label,
      placeholder: item.placeholder,
      isTextarea: item.isTextarea,
      data: item.data,
      children: [],
    };
    return acc;
  }, {});

  const root = [];

  flattenedItems.forEach((item) => {
    const node = idMapping[item.id];
    if (item.parent === null) {
      root.push(node);
    } else {
      const parentNode = idMapping[item.parent];
      if (parentNode) {
        parentNode.children.push(node);
      }
    }
  });

  return root;
}

function getProjection(
  items,
  activeId,
  overId,
  dragOffset,
  indentationWidth = 20,
) {
  const overItemIndex = items.findIndex((i) => i.id === overId);
  const activeItemIndex = items.findIndex((i) => i.id === activeId);

  if (overItemIndex === -1 || activeItemIndex === -1) return null;

  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = Math.round(dragOffset / indentationWidth);
  const tentativeDepth = Math.max(0, activeItem.depth + dragDepth);

  const maxDepth = getMaxDepth(previousItem);
  const minDepth = getMinDepth(nextItem);

  let depth = Math.max(minDepth, Math.min(maxDepth, tentativeDepth));

  let parentId;
  if (depth === 0) {
    parentId = null;
  } else {
    parentId = getParentId(previousItem, depth, newItems);
  }

  return { depth, maxDepth, minDepth, parentId };
}

function getMaxDepth(previousItem) {
  return previousItem ? previousItem.depth + 1 : 0;
}

function getMinDepth(nextItem) {
  return nextItem ? nextItem.depth : 0;
}

function getParentId(previousItem, depth, items) {
  if (!previousItem) return null;

  let current = previousItem;
  let currentIndex = items.findIndex((i) => i.id === current.id);

  while (current && current.depth >= depth && currentIndex > 0) {
    currentIndex -= 1;
    current = items[currentIndex];
  }

  return current ? current.id : null;
}

function removeFromTree(items, id) {
  return items.filter((item) => {
    if (item.id === id) return false;
    if (item.children) {
      item.children = removeFromTree(item.children, id);
    }
    return true;
  });
}

function getAllFields(items) {
  return items.reduce(
    (acc, item) => [...acc, item, ...getAllFields(item.children || [])],
    [],
  );
}

function hydrateContent(content, generateId) {
  if (!Array.isArray(content)) return [];
  return content
    .map((node) => {
      const config = fieldConfigs[node.type];
      if (!config) return null;
      const data = node.data || "";
      return {
        id: generateId(),
        ...config,
        data,
        children: node.children
          ? hydrateContent(node.children, generateId)
          : [],
      };
    })
    .filter(Boolean);
}

export default function AddEvent({
  isEdit = false,
  eventId = null,
  onSuccess = () => {},
}) {
  const generateId = () =>
    crypto.randomUUID?.() || Math.random().toString(36).substring(2, 9);
  const [fields, setFields] = useState({ allFields: [] });
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    ogImage: "",
  });

  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const flattenedFields = useMemo(
    () => flattenTree(fields.allFields),
    [fields.allFields],
  );
  const allFieldsItems = useMemo(
    () => getAllFields(fields.allFields),
    [fields.allFields],
  );

  useEffect(() => {
    if (isEdit && eventId) {
      fetchEventById(eventId);
    } else {
      const defaultFields = [
        { id: generateId(), ...fieldConfigs.title, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.location, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.date, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.time, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.mainImage, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.subtitle, data: "", children: [] },
        {
          id: generateId(),
          ...fieldConfigs.description,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.description,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.keyFeaturesSubtitle,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.keyFeatures,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.keyFeatures,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.keyFeatures,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.keyFeatures,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.additionalImage,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.additionalImage,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.venueLocation,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.contactPhone,
          data: "",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.contactEmail,
          data: "",
          children: [],
        },
        { id: generateId(), ...fieldConfigs.mapUrl, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.tag, data: "", children: [] },
      ];
      setFields({ allFields: defaultFields });
    }
    fetchCategories();
  }, [isEdit, eventId]);

  const fetchEventById = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`/api/event/${id}`);
      const eventObj = resp?.data?.data || resp?.data;
      console.log(eventObj);

      if (eventObj && eventObj.content) {
        const hydrated = hydrateContent(eventObj.content, generateId);
        setFields({ allFields: hydrated });
        setSelectedCategory(eventObj.category?._id || "");
        setSeoData({
          metaTitle: eventObj.metaTitle || "",
          metaDescription: eventObj.metaDescription || "",
          metaKeywords: eventObj.metaKeywords || "",
          ogImage: eventObj.ogImage || "",
        });
      } else {
        toast.error("Invalid event data to edit");
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      toast.error("Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const resp = await axios.get("/api/event/category/categories");
      setCategories(resp.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories");
    }
  };

  const removeField = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const areAllFieldsRemoved = () => fields.allFields.length === 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverId(active.id);
    setOffsetLeft(0);
  };

  const handleDragMove = ({ delta, over }) => {
    setOffsetLeft(delta.x);
    if (over) {
      setOverId(over.id);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (projected && over) {
      const { depth, parentId } = projected;
      const cloned = [...flattenedFields];
      const overIndex = cloned.findIndex((i) => i.id === over.id);
      const activeIndex = cloned.findIndex((i) => i.id === active.id);
      const activeTreeItem = cloned[activeIndex];
      cloned[activeIndex] = { ...activeTreeItem, depth, parent: parentId };
      const newItems = arrayMove(cloned, activeIndex, overIndex);
      setFields((prev) => ({
        allFields: buildTree(newItems),
      }));
    }
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  useEffect(() => {
    if (activeId && overId) {
      setProjected(
        getProjection(flattenedFields, activeId, overId, offsetLeft, 20),
      );
    } else {
      setProjected(null);
    }
  }, [activeId, overId, offsetLeft, flattenedFields]);

  const addField = (fieldType) => {
    setFields((prev) => {
      const config = fieldConfigs[fieldType];
      if (!config) return prev;
      if (["metaTitle", "metaDescription"].includes(fieldType)) {
        if (
          getAllFields(prev.allFields).some((item) => item.type === fieldType)
        ) {
          toast.warn(`Only one ${config.label} is allowed`);
          return prev;
        }
      }
      const newId = generateId();
      const newField = {
        id: newId,
        ...config,
        data: "",
        children: [],
      };
      return { allFields: [...prev.allFields, newField] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const contentCopy = JSON.parse(JSON.stringify(fields.allFields));
    const formData = new FormData();

    const traverseSetValues = (nodes) => {
      return nodes.map((node) => {
        const newNode = { type: node.type, id: node.id };
        if (
          [
            "title",
            "location",
            "date",
            "time",
            "subtitle",
            "description",
            "keyFeatures",
            "keyFeaturesSubtitle",
            "venueLocation",
            "contactPhone",
            "contactEmail",
            "mapUrl",
            "tag",
            "metaTitle",
            "metaDescription",
          ].includes(node.type)
        ) {
          const el = document.getElementById(`${node.type}-${node.id}`);
          if (!el) {
            throw new Error(`Missing element for ${node.type}-${node.id}`);
          }
          const value = el.value?.trim();
          if (!value) {
            toast.error(`${node.label} cannot be empty`);
            throw new Error(`${node.label} is required`);
          }
          newNode.data = value;
        } else if (["mainImage", "additionalImage"].includes(node.type)) {
          const urlEl = document.getElementById(`url-${node.type}-${node.id}`);
          const url = urlEl?.value?.trim();
          const fileInput = document.getElementById(
            `image-${node.type}-${node.id}`,
          );
          if (url) {
            newNode.data = url;
          } else if (fileInput?.files?.[0]) {
            formData.append(
              `image-${node.type}-${node.id}`,
              fileInput.files[0],
            );
            newNode.data = "";
          } else {
            newNode.data = node.data || "";
          }
        }
        if (node.children?.length) {
          newNode.children = traverseSetValues(node.children);
        }
        return newNode;
      });
    };

    let contentArray;
    try {
      contentArray = traverseSetValues(contentCopy);
    } catch (err) {
      console.error("Validation error:", err);
      return;
    }

    formData.append("content", JSON.stringify(contentArray));
    formData.append("category", selectedCategory);
    formData.append("metaTitle", seoData.metaTitle);
    formData.append("metaDescription", seoData.metaDescription);
    formData.append("metaKeywords", seoData.metaKeywords);
    formData.append("ogImage", seoData.ogImage);

    try {
      let resp;
      if (isEdit) {
        resp = await axios.put(`/api/event/${eventId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Event updated successfully!");
      } else {
        resp = await axios.post("/api/event/add-event", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Event added successfully!");
      }
      setFields({ allFields: [] });
      setSelectedCategory("");
      onSuccess();
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(
        `Failed to ${isEdit ? "update" : "add"} event: ${err.message}`,
      );
    }
  };

  const activeItem = activeId
    ? flattenedFields.find((i) => i.id === activeId)
    : null;

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    {isEdit ? "Edit Event" : "Add Event"}
                  </h4>
                </div>
                <form onSubmit={handleSubmit} className="flex-auto p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {Array(5)
                        .fill()
                        .map((_, idx) => (
                          <div
                            key={idx}
                            className="animate-pulse bg-gray-200 dark:bg-gray-700 h-16 rounded-md"
                          ></div>
                        ))}
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragMove={handleDragMove}
                      onDragEnd={handleDragEnd}
                      onDragCancel={handleDragCancel}
                    >
                      <SortableContext
                        id="root"
                        items={fields.allFields.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {areAllFieldsRemoved() ? (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            No fields have been added. Please add fields using
                            the buttons below.
                          </p>
                        ) : (
                          fields.allFields.map((item) => (
                            <TreeItem
                              key={item.id}
                              item={item}
                              depth={0}
                              removeField={removeField}
                              allFieldsItems={allFieldsItems}
                              activeId={activeId}
                              projected={projected}
                            />
                          ))
                        )}
                      </SortableContext>
                      <DragOverlay>
                        {activeId && activeItem ? (
                          <FieldInput
                            id={activeItem.id}
                            type={activeItem.type}
                            label={activeItem.label}
                            index={null}
                            placeholder={activeItem.placeholder}
                            isTextarea={activeItem.isTextarea}
                            removeField={() => {}}
                            dragListeners={{}}
                            depth={
                              projected ? projected.depth : activeItem.depth
                            }
                            data={activeItem.data}
                          />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}

                  <div className="mb-4 mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <SEOSection formData={seoData} onChange={handleSeoChange} />

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      disabled={areAllFieldsRemoved() || loading}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="ml-3 px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      onClick={() => {
                        setFields({ allFields: [] });
                        setSelectedCategory("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                <div className="p-6 pt-0">
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Add Fields
                    </h5>
                    <div className="flex flex-wrap gap-3">
                      {Object.keys(fieldConfigs).map((fieldType) => (
                        <button
                          key={fieldType}
                          onClick={() => addField(fieldType)}
                          className="px-4 py-2 bg-transparent border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white"
                        >
                          Add {fieldConfigs[fieldType].label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
